import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Button, IconButton } from "./ds";
import { useModalBackClose, useOverlayZ, useScrollLock } from "./hooks";
import "./modal.css";

// Strip a leading emoji/symbol prefix from a title so the serif heading starts on a letter.
export const cleanTitle = (t) => String(t || "").replace(/^[^\p{L}„"]+/u, "").trim();

export function Modal({ title, icon, avatar, subtitle, onClose, children, maxWidth = 480, headerAction, headerEnd, bigTitle, onIconClick, iconNode, iconStyle, center, viewportPinned, noKbPin, cardStyle, tourAttr, bodyClassName, footer }) {
  useScrollLock();
  useModalBackClose(onClose);  // v32.23 — Back closes this modal instead of navigating away
  const overlayZ = useOverlayZ();  // f-batch #1 — vnorený modal/sheet je vždy NAD tým, z ktorého vzišiel
  // v25 §10.14 — keď obsah pretečie, natiahni modal na celú obrazovku a až potom scrolluj vnútri
  const bodyRef = useRef(null);
  const [tall, setTall] = useState(false);
  useEffect(() => {
    const el = bodyRef.current;
    if (el && !tall && !viewportPinned && el.scrollHeight > el.clientHeight + 4) setTall(true);
  });
  // #2h — when the on-screen keyboard opens, make even a SHORT modal fill the height above the
  // keyboard (like the long habit modal already does), so its header stays pinned at the top and
  // only the body scrolls — instead of the whole card being shoved up when a lower field is focused.
  // Touch-only (desktop has no on-screen keyboard); reactive so a short modal shrinks back on blur.
  const [kbOpen, setKbOpen] = useState(false);
  useEffect(() => {
    const touch = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    if (viewportPinned || !touch) return undefined;
    const isField = (el) => el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
    if (isField(document.activeElement)) setKbOpen(true);
    const onIn = (e) => { if (isField(e.target)) setKbOpen(true); };
    const onOut = () => { setTimeout(() => { if (!isField(document.activeElement)) setKbOpen(false); }, 250); };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => { document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
  }, [viewportPinned]);
  // v26.3 / #2h — pin the card to the VISUAL viewport (top+height) so on iOS the keyboard-avoidance
  // scroll can't drag the whole modal up (the "slides up on first tap" bug) — the header stays at the
  // top of the visible area and only the body scrolls. Always for the AI chat (viewportPinned); for
  // every other form modal only WHILE the keyboard is open (kbOpen), so at rest it's a normal sheet.
  const [vv, setVv] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return undefined;
    const v = window.visualViewport;
    const update = () => setVv({ top: Math.max(0, v.offsetTop), height: v.height });
    update();
    v.addEventListener("resize", update);
    v.addEventListener("scroll", update);
    // v30 — iOS: po návrate z iného tabu ostane `vv` na starých hodnotách, takže pripnutá karta
    // visí na nesprávnom mieste. Domeriame, až keď sa viewport ustáli.
    const onRestore = () => {
      if (document.visibilityState !== "visible") return;
      [60, 250, 600].forEach((ms) => setTimeout(update, ms));
    };
    document.addEventListener("visibilitychange", onRestore);
    window.addEventListener("pageshow", onRestore);
    return () => {
      v.removeEventListener("resize", update); v.removeEventListener("scroll", update);
      document.removeEventListener("visibilitychange", onRestore);
      window.removeEventListener("pageshow", onRestore);
    };
  }, []);
  // v31 — spodný sheet (create/edit) sa pri klávesnici NEPINUJE (ostáva pri spodku, footer pod
  // klávesnicou); pinuje sa len AI chat (viewportPinned) a centrované dialógy pri otvorenej klávesnici.
  // v33h — `noKbPin`: malý centrovaný dialóg (napr. výber nálady) sa pri klávesnici NEROZŤAHUJE
  // na celú obrazovku; ostáva kompaktný (pole si scrollIntoView aj tak drží nad klávesnicou).
  const pinnedStyle = (viewportPinned || (kbOpen && center && !noKbPin))
    ? { position: "fixed", left: 0, right: 0, top: (vv ? vv.top : 0), height: (vv ? vv.height : "100dvh"), maxHeight: "none", borderRadius: 0, margin: "0 auto" }
    : null;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // v15 §7 — when a field inside the modal gains focus (keyboard opens), scroll it into view so it
    // isn't hidden behind the keyboard (the wrap already lifts the card above it via --kb-inset).
    const onFocusIn = (e) => {
      if (viewportPinned) return;  // pinned chat sa rieši cez visual viewport — netreba scrollIntoView
      const el = e.target;
      if (el && typeof el.scrollIntoView === "function" && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) {
        setTimeout(() => { try { el.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch { /* noop */ } }, 250);
      }
    };
    document.addEventListener("focusin", onFocusIn);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("focusin", onFocusIn); };
  }, [onClose, viewportPinned]);
  // Portal to <body> so a Modal opened inside another modal/transformed card isn't clipped by an
  // ancestor's overflow:hidden + animation transform (e.g. the icon picker inside the habit form).
  return createPortal(
    <div className={`bz-modal-scrim${center ? " bz-modal-scrim--center" : ""}`} style={{ "--bz-overlay-z": overlayZ }} onClick={onClose}>
      <div className={`bz-modal-card${center ? " bz-modal-card--center" : ""}${tall ? " bz-modal-card--tall" : ""}`}
        /* v32.21 — JEDNOTNÁ šírka pre všetky modaly (konzistencia): štandard 480 (= plná šírka na
           mobile, stĺpec na desktope). `maxWidth` sa už nezužuje pod štandard — clamp na min. 480.
           v33 — `cardStyle` (voliteľné) prefarbí CELÚ kartu vrátane hlavičky/pätičky naraz: hlavička
           a telo nemajú vlastné pozadie, len dedia z karty, takže jeden override stačí (napr.
           level-up modal farebný podľa tieru — pozri LevelUpModal).
           v32.23 — horná „úchytka" (drag handle) odstránená: modal sa nedá ťahať dole, čiara mýlila. */
        style={{ maxWidth: `min(${Math.max(maxWidth, 480)}px, 480px, 100vw)`, ...(pinnedStyle || {}), ...(cardStyle || {}) }}
        onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" {...(tourAttr || {})}>
        {bigTitle ? (
          /* v19 — eyebrow tag next to the icon, large serif title on its own line below (mock #17) */
          <div className="bz-modal-head--big">
            <div className="bz-modal-headrow">
              {avatar ? <span className="bz-modal-chip bz-modal-chip--avatar">{avatar}</span>
                : icon && <span className="bz-modal-chip"><BuzerIcon name={icon} size={20} color="var(--text)" /></span>}
              {subtitle ? <span className="bz-modal-sub" style={{ flex: 1, minWidth: 0, marginTop: 0 }}>{subtitle}</span> : <span className="bz-modal-spacer" />}
              {headerAction}
              <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Zavrieť"><Icon name="close" size={18} color="currentColor" /></IconButton>
            </div>
            <div className="bz-modal-title bz-modal-title--big">{cleanTitle(title)}</div>
          </div>
        ) : (
        <div className="bz-modal-head">
          {onIconClick ? (
            <button type="button" onClick={onIconClick} className="bz-modal-chip bz-modal-chip--btn" style={iconStyle} aria-label="Zmeniť ikonu" title="Zmeniť ikonu">
              {iconNode || (icon && <BuzerIcon name={icon} size={20} color="var(--text)" />)}
            </button>
          ) : avatar ? (
            <span className="bz-modal-chip bz-modal-chip--avatar">{avatar}</span>
          ) : (icon || iconNode) ? (
            <span className="bz-modal-chip" style={iconStyle}>{iconNode || <BuzerIcon name={icon} size={20} color="var(--text)" />}</span>
          ) : null}
          <span className="bz-modal-titlewrap">
            <span className="bz-modal-title">{cleanTitle(title)}</span>
            {subtitle && <span className="bz-modal-sub">{subtitle}</span>}
          </span>
          {headerAction}
          <span className="bz-modal-spacer" />
          {headerEnd}
          <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Zavrieť"><Icon name="close" size={18} color="currentColor" /></IconButton>
        </div>
        )}
        <div ref={bodyRef} className={`bz-modal-body${bodyClassName ? " " + bodyClassName : ""}`} style={viewportPinned ? { paddingBottom: 10, minHeight: 0 } : undefined}>{children}</div>
        {/* v33 — voliteľná sticky pätička (mimo scrollujúceho tela) — vždy viditeľná pod obsahom */}
        {footer && <div className="bz-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body);
}

// v31 — zdieľané potvrdenie „zmazať / radšej archivovať" — JEDNO miesto pre návyk, výzvu, skupinu…
// Archív = primary tlačidlo (bezpečnejšia voľba hore), pod ním Zrušiť + Zmazať (destructive).
export function ConfirmDelete({ title = "Zmazať?", icon = "trash", iconNode, iconStyle, message,
  archiveLabel = "Radšej archivovať", onArchive, deleteLabel = "Zmazať", onDelete, onClose }) {
  return (
    <Modal center title={title} icon={iconNode ? undefined : icon} iconNode={iconNode} iconStyle={iconStyle} maxWidth={400} onClose={onClose}>
      {message && <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>{message}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: message ? 16 : 4 }}>
        {onArchive && <Button variant="primary" block onClick={onArchive}><BuzerIcon name="folder" size={16} color="currentColor" />{archiveLabel}</Button>}
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="ghost" block onClick={onClose}>Zrušiť</Button>
          <Button variant="destructive" block onClick={onDelete}><Icon name="delete" size={16} color="currentColor" />{deleteLabel}</Button>
        </div>
      </div>
    </Modal>
  );
}

// v31 — zdieľaná pätička create/edit formulárov (JEDNO miesto pre rozloženie tlačidiel).
// Sticky footer (cez .bz-modal-actions): primary (Uložiť/Pridať/Vytvoriť) hore na celú šírku,
// pod ním riadok — Vymazať (ak `onDelete`, vľavo) + Zrušiť (vpravo).
// Ak je tlačidlo v <form>, nechaj `onSubmit` prázdny (button type=submit); inak podaj `onSubmit`.
export function FormFooter({ submitLabel, submitDisabled, onSubmit, onCancel, cancelLabel = "Zrušiť", deleteLabel = "Vymazať", onDelete }) {
  return (
    <div className="bz-modal-actions" style={{ flexDirection: "column" }}>
      <Button variant="primary" block type={onSubmit ? "button" : "submit"} disabled={submitDisabled} onClick={onSubmit}>{submitLabel}</Button>
      {onDelete && <Button variant="destructive" block onClick={onDelete}><Icon name="delete" size={16} color="currentColor" />{deleteLabel}</Button>}
      <Button variant="ghost" block onClick={onCancel}>{cancelLabel}</Button>
    </div>
  );
}

// F-CreateModal — wrap a create form in the popup Modal; inline (no shell) when editing in place.
// `subtitle` — druhý riadok v hlavičke (v38: knižnica návykov ním ukazuje špecifikáciu
// vybraného návrhu, keďže jej formulár už nemá pole „Špecifikácia").
export function FormShell({ modal, title, subtitle, icon, onClose, children, headerAction, onIconClick, iconNode, iconStyle }) {
  return modal ? <Modal title={title} subtitle={subtitle} icon={icon} onClose={onClose} maxWidth={480} headerAction={headerAction} onIconClick={onIconClick} iconNode={iconNode} iconStyle={iconStyle}>{children}</Modal> : children;
}
